import { initializeApp } from 'firebase/app';
import Cookies from 'js-cookie';
import LocalForage from 'localforage';
import 'localforage-getitems';
import Vue from 'vue';

import type { ApolloProvider } from 'vue-apollo';
import type { Route } from 'vue-router';
import type { Store } from 'vuex';

import { Context } from '@/@types/ssr';

import { COUNTRY_EXCEPTIONS, WEB_LOCALES } from '@/constants/web-locales.constant';

import { OnboardingStatus } from '@/enums/onboarding-status';
import { WebLocale } from '@/enums/web-locale';

import { checkIp } from '@/helpers/ip-check-helper';
import { getLocaleFromBrowser } from '@/helpers/locale-helper';
import OnboardingHelper from '@/helpers/onboarding-helper';
import { captureMessageForSentry, Sentry } from '@/helpers/sentry-helper';
import StateHelper from '@/helpers/state-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import UserHelper from './helpers/user-helper';

import { createRouter } from '@/routing';
import { globalRoutes } from '@/routing/global';

import {
	TrackingProviderGoogleAnalytics,
	TrackingProviderGoogleTagManager,
	TrackingProviderSnowplow,
	TrackingProviderTwitterPixel,
} from '@/helpers/tracking/providers';
import { STORE_NAMESPACE } from '@/store';
import { ConstantType } from '@/stores/constant.store';
import { ReadyType } from '@/stores/ready.store';
import { getVm } from './helpers/vm-helper';

export {
	coreAppRequests,
	initApp,
	initClientStorage,
	initFirebase,
	initLanguage,
	initLocale,
	initRouter,
	initSPA,
	initSSR,
	initStore,
	initStoreWatchers,
	initTrackers,
	jwIdServerClientHandover,
	refreshToken,
};

function initFirebase() {
	try {
		const firebaseConfig = {
			apiKey: JW_CONFIG.FIREBASE_API_KEY,
			authDomain: JW_CONFIG.FIREBASE_AUTH_DOMAIN,
			projectId: JW_CONFIG.FIREBASE_PROJECT_ID,
			storageBucket: JW_CONFIG.FIREBASE_STORAGE_BUCKET,
			messagingSenderId: JW_CONFIG.FIREBASE_MESSAGING_SENDER_ID,
			appId: JW_CONFIG.FIREBASE_APP_ID,
		};
		initializeApp(firebaseConfig);
	} catch (err) {
		const error: any = err;
		captureMessageForSentry(
			'Firebase Init Issue:',
			{ error, where: '[main.common.ts] initFirebase()' },
			Sentry.Severity.Error
		);
	}
}

/**
 * Client Only Method
 *
 * @param context
 */
async function refreshToken(context: Context) {
	const { store } = context;
	// This order is important, we want to try refresh the token before initializing the store
	// and making the requests to loadList and loadSettings. The user would be logged out if the refreshToken fails
	if (store.getters['user/isLoggedIn']()) {
		const token = await store.dispatch('user/refreshToken');

		// If token isn't returned then there was an error returning token
		if (!token) {
			context.showRefreshTokenError = true;

			TrackingHelper.trackEvent('refresh_token_error', {
				action: 'main_ssr_refresh',
			});
		}
	}

	if (store.state.user.accessToken) {
		const user = await UserHelper.currentUser();

		if (!user) {
			const exchangedToken = await UserHelper.exchangeToken(store);
			if (!exchangedToken) {
				context.showRefreshTokenError = true;
			}
		}
	}
}

async function initClientStorage() {
	let driver;
	if (LocalForage.supports(LocalForage.LOCALSTORAGE)) {
		driver = LocalForage.LOCALSTORAGE;
	}

	if (!driver) {
		throw new Error(`[Local storage] local forage drivers not available`);
	}

	await LocalForage.config({
		name: STORE_NAMESPACE,
		driver: driver,
	});
}

async function initApp(context: Context) {
	const { store, webLocale } = context;
	// these requests will be checked first, if they have been done on the server already and will skip accordingly
	if (process.server || !process.ssr) {
		const { coreAppRequests } = await import('@/main.common');
		await coreAppRequests(store);

		await store.dispatch('sponsoredRecommendations/setSponsoredRecommendationCohort');
	}

	if (!context.store.state.user.onboarding_status) {
		const isLoggedIn = store.getters['user/isLoggedIn']();

		const onboardingStatus = OnboardingHelper.getOnboardingStatus(
			webLocale,
			store.state.user.settings.taste_survey_completed,
			isLoggedIn,
			context.url,
			store.state.user.settings.taste_survey_type
		);

		store.dispatch('user/saveOnboardingStatus', onboardingStatus);
	}
}

async function initRouter(context: Context) {
	const doEveryPageView = async (context: Context, to: Route, from?: Route) => {
		const { meta, fullPath } = to;

		// temporary fix for dev env @to-do fix server
		if (((from?.name !== 'app.refreshtoken' && !to.name && to.path !== '/') || to.path.indexOf('/refresh')) === 0) {
			// Avoid Routing when
			return;
		}

		// if route change reset pull state before setting route
		const shouldPull = to.name !== from?.name;
		if (shouldPull) {
			context.store.commit('pull/SET_PULLS', []);
		}

		// @note: setActiveRoute needs to happen before fetchUrlMetadata
		// maybe it's better to merge these 2, since they aren't called anywhere else, either
		context.store.dispatch('routing/setActiveRoute', { activeRoute: to, router: context.router });
		// we should tie pagesVisited to activeRoute.
		// with a flaky connection there is a chance that we transitioned to a new route (activeRoute),
		// but the pagesVisited hasn't reflected that because it's still awaiting urlMetadata.
		context.store.commit('tracking/INCREASE_PAGES_VISITED');
		if (to.path !== from?.path) {
			await context.store.dispatch('routing/fetchUrlMetadata', to.path);
		}

		// ###### TRACK PAGE ######
		// before, we awaited trackPage, to make sure the `tracking.queues` are filled with all necessary state.
		// but since we have overlapping awaits with `titleDetails/whenDetailTitleLoaded`, where loading the title detail happens in the component (therefore after `doEveryPageInit()`), we can't await on it.
		//
		// if there were any async actions in trackPage, that aren't resolved beforehand, trackPage wouldn't be able to finish writing into `tracking.queues` and send it over to __DATA__.
		// so here the async actions that happen in trackPage, that are already resolved beforehand and don't yield additional awaits:
		// - urlMetadata: resolved
		// - getContexts: only unresolved if there is an unknown pagetype
		// - titleDetails/whenDetailTitleLoaded: when 'title' is present in `routes.meta.contexts` it needs to be awaited, but will be resolved in TitleDetail.vue@serverPrefetch
		const metaContexts = (meta?.contexts || []) as string[];

		// we track page views ourselves for title detail pages and (nearly all) sport pages
		const sportPageExceptions = [
			// 'app.sports.all', // this isn't included because it doesn't have any sport context
			'app.sports.overview',
			'app.sports.competition', // @todo implement with sport context
			'app.sports.event',
		];
		if (!metaContexts.includes('title') && !sportPageExceptions.includes(to.name || '')) {
			TrackingHelper.trackPage(fullPath);
		}

		// @todo actually we'd need to await pull/loadPulls as well, otherwise there's a chance the server doesn't put pulls in the __DATA__.
		context.store.dispatch('pull/loadPulls');
	};

	// check for first routing and possibly update store state from url (e.g. filters from url)
	context.router.beforeEach(async (to: Route, from: Route, next: () => void) => {
		const firstRouting = from?.name === null || from?.name === 'app.refreshtoken';
		// persist scroll position for each tab
		if (!firstRouting && process.client) {
			const tab = from?.meta?.tab === 'sports' ? `${from?.meta?.tab}-${from?.meta?.data}` : from?.meta?.tab;
			context.store.commit('routing/SET_SCROLL_POSITION', { tab, y: window.scrollY });
		}

		// fire doEveryPageView only when:
		// - on the server
		// OR
		// - is not first routing `!firstRouting`
		// OR
		// - (first routing `firstRouting` AND activeRoute hasn't been set by the server `!context.store.state.routing.activeRoute`)
		// OR
		// - is SPA app PLATFORM.IS_SPA
		if (
			process.server ||
			!firstRouting ||
			(firstRouting && !context.store.state.routing.activeRoute) ||
			PLATFORM.IS_SPA
		) {
			const promise = doEveryPageView(context, to, from);
			if (process.server) {
				// we only need the server to await the promise, the client can move forward already
				await promise;
			}
		}

		if (firstRouting && process.client) {
			// NOTE: deeplinking an experiment might show hydration errors around the experiment component
			await StateHelper.applyExperimentFromRoute(to, context.store);
		}

		// check first page view
		if ((firstRouting && process.server) || (process.client && to.path !== from.path)) {
			// @todo translate route parameters to filters if there are any
			await StateHelper.applyRoute(to, context.store);
		}

		next();
	});

	context.router.afterEach(async (to: { meta: { onActivate: any } }, from: any) => {
		const onActivate = to.meta.onActivate;
		// call activation function from routes
		if (onActivate) {
			onActivate(context.store, to, from);
		}
	});
}
async function initStore(context: Context) {
	const { store, ssrData, webLocale } = context;

	// set up usefull value for the client
	if (process.server) {
		store.dispatch('app/setSeoUser', ssrData.isSeoUser ?? true);
		store.dispatch('app/setGeoLocation', ssrData.geoLocation);
	}

	// NOTE(valerio.mazza): failsafe for filter.store to remove after a proper refactor of the filter.store
	if (!store.state.filter[webLocale]) {
		// init filter here before setting webLocale for reactivity reason (webLocale is watched everywhere)
		store.dispatch('filter/initFiltersPerLocale', webLocale, { root: true });
	}

	if (store.state.user.jwId) {
		Vue.$jw.ready?.setReady(ReadyType.JW_ID);
	}

	// CONSENT_PARTNERS_ANSWERED is only applicable to GDPR countries with Usercentrics.
	// that's why we set it to ready immediately, so other areas in the web app are not awaiting it forever.
	if (!TrackingHelper.isGDPRCountry(context.webLocale as WebLocale)) {
		Vue.$jw.ready?.setReady(ReadyType.CONSENT_PARTNERS_ANSWERED);
	}

	// only fetch user settings and lists when:
	// - is not Seo User (meaning brand new jw_id, probably coming from a search engine)
	// - jwId is present
	// - AND
	// - is server OR (is client AND SSR store (window.__DATA__) is not present)

	if (
		!ssrData.isSeoUser &&
		store.state.user.jwId &&
		(process.server || (process.client && !(window as any).__DATA__))
	) {
		await store.dispatch('user/loadSettings');
	} else {
		// Unlock ready for USER_SETTINGS_LOADED
		// Note(valerio.mazza): look into removing this alltogether
		Vue.$jw.ready?.setReady(ReadyType.USER_SETTINGS_LOADED);
	}
}

async function initSPA(context: Context, apolloProvider: ApolloProvider): Promise<any[]> {
	// set Default
	context.ssrData = {
		geoLocation: undefined,
		isSeoUser: false,
	};

	const promises = [];

	// initialize id service
	if (!context.store.state.user.jwId) {
		promises.push(context.store.dispatch('user/fetchJwId', apolloProvider));
	} else {
		promises.push(context.store.dispatch('user/setJwId', context.store.state.user.jwId));
	}

	promises.push(
		new Promise(async (resolve, reject) => {
			try {
				const data = await checkIp();
				const webLocale = COUNTRY_EXCEPTIONS[data.webLocale] ?? data.webLocale;

				if (WEB_LOCALES[webLocale as WebLocale]) {
					context.ssrData = {
						geoLocation: webLocale,
						isSeoUser: false,
					};
				}

				resolve(true);
			} catch (err) {
				// silence check ip errors
				resolve(false);
			}
		})
	);

	return Promise.all(promises);
}

const initSSR = (context: Context) => {
	context.ssrData = {
		geoLocation: null,
		isSeoUser: false,
	};

	if (process.server) {
		const region = context.req.headers['x-client-geo-region'];
		context.ssrData.isSeoUser = context.req.headers['x-seo-user'] === 1 ? true : false;
		context.ssrData.geoLocation = region?.toLowerCase();
	}
};

function loadConstants(store: Store<any>) {
	return [ConstantType.PROVIDERS, ConstantType.GENRES].map(filterType =>
		store.dispatch('constant/fetchConstant', filterType)
	);
}

function jwIdServerClientHandover(store: Store<any>) {
	// client-side jw_id handover
	const jwId = Cookies.get('jw_id');
	if (jwId) {
		return store.dispatch('user/setJwId', jwId);
	}
}

async function initStoreWatchers(context: Context) {
	const { i18n, store, router, url } = context;

	// #### webLocale ####
	store.watch(
		(state: any) => state.language.webLocale,
		async (webLocale: WebLocale, oldWebLocale: WebLocale | undefined) => {
			if (webLocale !== oldWebLocale && !store.state.filter[webLocale]) {
				store.dispatch('filter/initFiltersPerLocale', webLocale);
			}

			if (webLocale !== oldWebLocale) {
				if (!router) {
					// change locale or create router (and change locale)
					context.router = createRouter(webLocale, store);
				} else {
					// router is already initialized, change country on the fly
					router.changeLocale(webLocale, store);
				}

				initLanguage({ i18n, store, webLocale, router });

				store.dispatch('experiment/fetchExperimentsConfig');
				// Experiment Data

				store.dispatch('sponsoredRecommendations/setSponsoredRecommendationCohort');

				loadConstants(store);
			}
		},
		{ immediate: false }
	);

	// #### overriddenLanguage ####
	store.watch(
		(state: any) => state.language.overriddenLanguage,
		async (language: string, oldLanguage: string | undefined) => {
			if (language) {
				const activeRoute = getVm()?._route ?? router?.match(context.url);
				await store.dispatch('language/fetchTranslation', { language, activeRoute });
				await store.dispatch('language/setLanguage', { language, i18n });

				store.dispatch('experiment/fetchExperimentsConfig');
			}
		}
	);

	// watch accessToken when user login set onboarding accordingly
	store.watch(
		(state: any) => state.user.accessToken,
		async (value: string) => {
			if (value) {
				await store.dispatch('user/loadSettings');

				const onboardingStatus = OnboardingHelper.getOnboardingStatus(
					store.state.language.detectedWebLocale || store.state.language.webLocale,
					store.state.user.settings.taste_survey_completed,
					true,
					context.url
				);

				store.dispatch('user/saveOnboardingStatus', onboardingStatus);
			}
		}
	);

	// watch loggedInProviders when user login set onboarding accordingly
	store.watch(
		(state: any) => state.user.loggedInProviders,
		async (providers: Array<any>, oldProviders: Array<any> | undefined) => {
			if (providers.length > 0 && store.state.user.settings.taste_survey_completed) {
				store.dispatch('user/saveOnboardingStatus', OnboardingStatus.USER);
			} else if (providers.length > 0 && !store.state.user.settings.taste_survey_completed) {
				store.dispatch('user/saveOnboardingStatus', OnboardingStatus.SIGNED_UP);
			}
		},
		{ immediate: false }
	);

	/* *************************** Immediate watchers ****************************/

	// #### onboarding_status ####
	store.watch(
		(state: any) => state.user.onboarding_status,
		async (onboardingStatus: OnboardingStatus, oldOnboardingStatus: OnboardingStatus | undefined) => {
			if (onboardingStatus && url === '/') {
				if (
					onboardingStatus !== OnboardingStatus.TASTE_ONBOARDING &&
					onboardingStatus !== oldOnboardingStatus
				) {
					TrackingHelper.trackEvent('home_page_view', {
						action: onboardingStatus,
						property: store.state.language.detectedWebLocale || store.state.language.webLocale,
						nonInteraction: true,
					});
				}
			}
		},
		{ immediate: true }
	);

	// #### uc_consents ####
	store.watch(
		(_state: any, getters: any) => getters['user/allConsentPartnersAnswered'],
		(allConsentPartnersAnswered: boolean) => {
			if (allConsentPartnersAnswered) {
				Vue.$jw.ready?.setReady(ReadyType.CONSENT_PARTNERS_ANSWERED);
			}
		},
		{ immediate: true }
	);
}

/*************       initLocale        *************/

async function initLocale(context: Partial<Context>) {
	let initAppLocale: WebLocale;
	const { store, url = '' } = context;
	const { language } = store.state;

	if (!language.locales.length) {
		await store.dispatch('language/loadLocales');
	}

	const path = url.split('?')[0];
	const slug = /^\/[\w\d]+/.exec(url)?.[0] || '';
	const isGlobalRoute = globalRoutes.includes(path) || globalRoutes.includes(slug);

	if (isGlobalRoute) {
		initAppLocale = await getWebLocaleFromLocation(context);
	} else {
		initAppLocale = await getWebLocaleFromPath(context, path);
	}

	await store.dispatch('language/changeWebLocale', initAppLocale);

	Vue.$jw.ready?.setReady(ReadyType.WEBLOCALE_SET);

	return initAppLocale;
}

async function getWebLocaleFromLocation(context: Partial<Context>) {
	const { store, ssrData } = context;
	const { language, user } = store.state;

	let locale = user.persistedWebLocale || (process.client && language.detectedWebLocale);

	// if not found OR if SPA app
	// get the injected geoLocation
	if ((!locale && process.server) || PLATFORM.IS_SPA) {
		locale = ssrData?.geoLocation;
	}

	if (!locale && process.client) {
		// get Browser Locale as last resort
		locale = getLocaleFromBrowser() as WebLocale;
	}

	if (!locale) {
		// hard fallback to US just in case
		locale = WebLocale.us;
	}

	store.dispatch('language/setDetectedWebLocale', locale);
	return language.detectedWebLocale;
}

async function getWebLocaleFromPath(context: Partial<Context>, path: string) {
	const { store } = context;
	const locale = path && path.length >= 3 && path.split('/')[1];

	const webLocale = locale && COUNTRY_EXCEPTIONS[locale] ? COUNTRY_EXCEPTIONS[locale] : locale;
	await store.dispatch('language/setDetectedWebLocale', webLocale);
	return store.state.language.detectedWebLocale;
}

/*************       initLanguage        *************/

async function initLanguage(context: Partial<Context>): Promise<any> {
	const { i18n, store, webLocale, router } = context;
	let language = store.state.user.persistedOverriddenLanguage;

	// Get language from localStorage or detect browser language
	if (store.state.user.persistedOverriddenLanguage) {
		language = store.state.user.persistedOverriddenLanguage;
		store.dispatch('language/setDetectedLanguage', language);
	} else {
		store.dispatch('language/setLanguageFromLocale', webLocale);
		language = store.state.language.overriddenLanguage;
	}

	// Hard fall back to en
	if (!language) {
		language = 'en';

		captureMessageForSentry('[Language] not able to detect language, hard fallback to en', {
			where: '[main.common.ts] initLanguage Failed to init',
		});
	}

	const activeRoute = router?.match(context.url);
	await store.dispatch('language/fetchTranslationAndSetLanguage', { i18n, language, activeRoute });
	await store.dispatch('meta/setAppDirection', { language });

	return language;
}

/**
 *
 */
async function coreAppRequests(store: Store<any>) {
	let parallelRequests: Promise<any>[] = [];

	// Experiments
	if (!Object.keys(store.state.experiment.experiments).length) {
		parallelRequests.push(store.dispatch('experiment/fetchExperimentsConfig'));
	}

	// Constants
	parallelRequests = [...parallelRequests, ...loadConstants(store)];

	return Promise.all(parallelRequests);
}

async function initTrackers(store: Store<any>, force: boolean = false) {
	const ConsentHelper = await (
		await import(/* webpackChunkName: "constent-helper" */ '@/helpers/consent-helper')
	).default;

	try {
		TrackingHelper.setStore(store);

		const consents = store.state.user.settings?.jw_consents || {};
		const deviceDoNotTrack = false;
		const jwId = store.state.user.jwId;
		let snowPlowJwId = jwId;

		if (!snowPlowJwId) {
			const randDigits = Array(17)
				.fill(0)
				.map(x => Math.random().toString(36).charAt(2))
				.join('');
			snowPlowJwId = `fake-${randDigits}`;
		}

		// initialize trackers
		// pass over the instance to the store for snowplow provider beeing
		// able to compile a list of contexts from global state

		const { platform, version } = store.state.app;
		const { TrackingProviderFacebookPixel, TrackingProviderTiktokPixel } = await import(
			/* webpackChunkName: "tracking-providers" */ '@/helpers/tracking/providers'
		);

		await Vue.$jw.ready?.waitFor(ReadyType.EXPERIMENTS_LOADED);

		// initialize first with trackers that on web and mobile
		const trackers = [
			// SNOWPLOW
			new TrackingProviderSnowplow({
				userId: snowPlowJwId,
				appId: `${version}-${platform}`,
				cookieDomain: global.JW_CONFIG.SNOWPLOW_COLLECTOR_ENDPOINT,
				platform: 'web',
				doNotTrack: ConsentHelper.hasDoNotTrack(deviceDoNotTrack, consents),
				eventMethod: 'post',
			}),
			// FACEBOOK PIXEL
			new TrackingProviderFacebookPixel({
				pixelId: '416036085213374',
				doNotTrack: ConsentHelper.hasDoNotTrack(deviceDoNotTrack, consents, ['facebook']),
			}),
			// TIKTOK PIXEL
			new TrackingProviderTiktokPixel({
				pixelId: 'C5O26T5O3VNUQLVLL8P0',
				doNotTrack: ConsentHelper.hasDoNotTrack(deviceDoNotTrack, consents, ['tiktok']),
			}),
			// GOOGLE ANALYTICS
			new TrackingProviderGoogleAnalytics({
				id: ['UA-58489323-1', 'UA-58489323-4'],
				debug: {
					enabled: false, // process.env.NODE_ENV === 'development',
				},
				doNotTrack: ConsentHelper.hasDoNotTrack(deviceDoNotTrack, consents, ['google']),
				userId: jwId,
			}),
			// GOOGLE TAG MANAGER
			new TrackingProviderGoogleTagManager({
				containerId: 'GTM-53RWB5',
				doNotTrack: ConsentHelper.hasDoNotTrack(deviceDoNotTrack, consents, ['google']),
			}),
			new TrackingProviderTwitterPixel({
				pixelId: 'o0dvi',
				doNotTrack: ConsentHelper.hasDoNotTrack(deviceDoNotTrack, consents, ['twitter']),
			}),
		].filter(Boolean);

		TrackingHelper.initializeTrackers(trackers, force);
	} catch (err) {
		const error: any = err;
		captureMessageForSentry(
			'Main Init Issue:',
			{ error, where: '[main.common.ts] initializeTracking' },
			Sentry.Severity.Error
		);
	}
}
